/**
 * Store Locator - Google Maps Integration
 * Audi-style store finder with search and filtering
 */

// Map palette comes from CSS custom properties (section-store-locator.css -> base.css tokens).
// Google Maps needs literal colours at runtime, so we resolve them once here.
function slToken(name, fallback) {
  try {
    var v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
  } catch (e) { return fallback; }
}
// Resolve possible var()/color-mix() chains by letting the browser compute them on a probe element.
function slResolveColor(value, fallback) {
  try {
    var el = document.createElement('span');
    el.style.color = value;
    el.style.display = 'none';
    document.body.appendChild(el);
    var c = getComputedStyle(el).color;
    document.body.removeChild(el);
    if (!c) return fallback;
    // Computed colour may be rgb(), rgba() or color(srgb r g b / a) (color-mix results in Chrome).
    // Paint it on a 1x1 canvas over white and read the bytes back — format-agnostic, alpha composited.
    var cv = document.createElement('canvas');
    cv.width = 1; cv.height = 1;
    var ctx = cv.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 1, 1);
    ctx.fillStyle = c;
    if (ctx.fillStyle === '#ffffff' && value.indexOf('fff') === -1 && c.indexOf('255, 255, 255') === -1) {
      // parser rejected the string; fall back
      return fallback;
    }
    ctx.fillRect(0, 0, 1, 1);
    var d = ctx.getImageData(0, 0, 1, 1).data;
    function hex(n) { return ('0' + n.toString(16)).slice(-2); }
    return '#' + hex(d[0]) + hex(d[1]) + hex(d[2]);
  } catch (e) { return fallback; }
}
function slColor(name, fallback) { return slResolveColor('var(' + name + ')', fallback); }

function buildMapStyle() {
  var land = slColor('--sl-map-land', '#f2f1ef');
  var natural = slColor('--sl-map-natural', '#eeede9');
  var water = slColor('--sl-map-water', '#dfe6eb');
  var road = slColor('--sl-map-road', '#ffffff');
  var highway = slColor('--sl-map-highway', '#e6e4e1');
  var stroke = slColor('--sl-map-stroke', '#c9c9c9');
  var label = slColor('--sl-map-label', '#5f6368');
  var halo = slColor('--sl-map-label-halo', '#ffffff');
  return [
    { elementType: "geometry", stylers: [{ color: land }] },
    { elementType: "labels.text.fill", stylers: [{ color: label }] },
    { elementType: "labels.text.stroke", stylers: [{ color: halo }] },
    { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
    { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: stroke }] },
    { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
    { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: natural }] },
    { featureType: "poi", stylers: [{ visibility: "off" }] },
    { featureType: "road", elementType: "geometry", stylers: [{ color: road }] },
    { featureType: "road", elementType: "geometry.stroke", stylers: [{ visibility: "off" }] },
    { featureType: "road.arterial", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "road.highway", elementType: "geometry", stylers: [{ color: highway }] },
    { featureType: "road.highway", elementType: "labels", stylers: [{ visibility: "simplified" }] },
    { featureType: "road.local", stylers: [{ visibility: "off" }] },
    { featureType: "transit", stylers: [{ visibility: "off" }] },
    { featureType: "water", elementType: "geometry", stylers: [{ color: water }] },
    { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: label }] }
  ];
}

function StoreLocator(config) {
  this.config = config;
  this.stores = config.stores || [];
  this.map = null;
  this.markers = [];
  this.activeMarker = null;
  this.activeStore = null;
  this.userLocation = null;
  this.searchRadius = 50;
  this.filteredStores = this.stores.slice();
  this.autocomplete = null;
  this.elements = {};
  
  this.init();
}

StoreLocator.prototype.init = function() {
  this.cacheElements();
  this.bindEvents();
  this.initMap();
  this.renderStoreList(this.stores);
  this.updateStoreCount(this.stores.length);
};

StoreLocator.prototype.cacheElements = function() {
  this.elements = {
    map: document.getElementById('storeMap'),
    searchInput: document.getElementById('storeSearchInput'),
    searchClear: document.getElementById('searchClear'),
    useLocationBtn: document.getElementById('useLocationBtn'),
    radiusSelect: document.getElementById('radiusSelect'),
    storeList: document.getElementById('storeList'),
    storeCount: document.getElementById('storeCount'),
    radiusText: document.getElementById('radiusText'),
    resultsInfo: document.getElementById('resultsInfo'),
    searchPanel: document.getElementById('searchPanel'),
    detailPanel: document.getElementById('detailPanel'),
    detailContent: document.getElementById('detailContent'),
    backToResults: document.getElementById('backToResults'),
    loading: document.getElementById('storeListLoading'),
    empty: document.getElementById('storeListEmpty')
  };
};

StoreLocator.prototype.bindEvents = function() {
  var self = this;
  
  if (this.elements.searchInput) {
    var searchTimeout;
    this.elements.searchInput.addEventListener('input', function() {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(function() {
        self.handleSearch();
      }, 300);
    });
    
    this.elements.searchInput.addEventListener('keydown', function(e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        self.handleSearch();
      }
    });
  }
  
  if (this.elements.searchClear) {
    this.elements.searchClear.addEventListener('click', function() {
      self.clearSearch();
    });
  }
  
  if (this.elements.useLocationBtn) {
    this.elements.useLocationBtn.addEventListener('click', function() {
      self.getUserLocation();
    });
  }
  
  if (this.elements.radiusSelect) {
    this.elements.radiusSelect.addEventListener('change', function(e) {
      self.searchRadius = parseInt(e.target.value);
      if (self.userLocation) {
        self.filterByDistance(self.userLocation);
      }
    });
  }
  
  if (this.elements.backToResults) {
    this.elements.backToResults.addEventListener('click', function() {
      self.showSearchPanel();
    });
  }
};

StoreLocator.prototype.initMap = function() {
  var self = this;
  
  if (!this.elements.map) {
    console.error('Store Locator: Map container not found');
    return;
  }

  if (typeof google === 'undefined' || !google.maps) {
    console.error('Store Locator: Google Maps not loaded');
    return;
  }

  var mapOptions = {
    center: this.config.defaultCenter,
    zoom: this.config.defaultZoom,
    styles: buildMapStyle(),
    disableDefaultUI: true,
    zoomControl: true,
    zoomControlOptions: {
      position: google.maps.ControlPosition.RIGHT_CENTER
    },
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'cooperative'
  };
  
  this.map = new google.maps.Map(this.elements.map, mapOptions);
  
  // Markers carry the shop favicon; wait for it (fast, cached) so the first paint is already branded.
  var self = this;
  this.preloadFavicon(function() { self.addMarkers(); });
  this.initAutocomplete();
  
  if (this.elements.loading) {
    this.elements.loading.style.display = 'none';
  }

  console.log('Store Locator: Map initialized with ' + this.stores.length + ' stores');
};

StoreLocator.prototype.initAutocomplete = function() {
  var self = this;
  
  if (!google.maps.places || !this.elements.searchInput) return;
  
  this.autocomplete = new google.maps.places.Autocomplete(
    this.elements.searchInput,
    {
      types: ['(regions)'],
      componentRestrictions: { country: 'us' }
    }
  );
  
  this.autocomplete.addListener('place_changed', function() {
    var place = self.autocomplete.getPlace();
    if (place.geometry) {
      var location = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      };
      self.userLocation = location;
      self.map.setCenter(location);
      self.map.setZoom(10);
      self.filterByDistance(location);
    }
  });
};

// Load the shop favicon once (CORS-enabled so it can be drawn to canvas). Calls cb either way.
StoreLocator.prototype.preloadFavicon = function(cb) {
  var self = this;
  var url = this.config.faviconUrl;
  if (!url || this.faviconImg !== undefined) { cb(); return; }
  this.faviconImg = null;
  var img = new Image();
  img.crossOrigin = 'anonymous';
  var done = false;
  function finish() { if (done) return; done = true; cb(); }
  img.onload = function() { self.faviconImg = img; self.iconCache = {}; finish(); };
  img.onerror = function() { finish(); };
  img.src = url;
  setTimeout(finish, 1500); // never block markers on a slow favicon
};

StoreLocator.prototype.createMarkerIcon = function(color, isActive) {
  var size = isActive ? 39 : 31;
  var surface = slColor('--sl-marker-fill', '#ffffff');
  this.iconCache = this.iconCache || {};
  var key = color + '|' + (isActive ? 1 : 0);
  if (this.iconCache[key]) return this.iconCache[key];

  var icon = null;
  // Branded marker: surface disc, hairline ring, favicon inside. Drawn on canvas at 2x for crispness.
  if (this.faviconImg) {
    try {
      var dpr = 2;
      var cv = document.createElement('canvas');
      cv.width = size * dpr; cv.height = size * dpr;
      var ctx = cv.getContext('2d');
      ctx.scale(dpr, dpr);
      var c = size / 2;
      var ringW = isActive ? 2 : 1;
      var r = c - ringW;
      // disc
      ctx.beginPath(); ctx.arc(c, c, r, 0, Math.PI * 2);
      ctx.fillStyle = surface; ctx.fill();
      ctx.lineWidth = ringW; ctx.strokeStyle = color; ctx.stroke();
      // favicon, clipped to the disc, ~62% of the diameter
      var iw = size * 0.58;
      ctx.save();
      ctx.beginPath(); ctx.arc(c, c, r - ringW, 0, Math.PI * 2); ctx.clip();
      ctx.drawImage(this.faviconImg, c - iw / 2, c - iw / 2, iw, iw);
      ctx.restore();
      icon = {
        url: cv.toDataURL('image/png'),
        scaledSize: new google.maps.Size(size, size),
        anchor: new google.maps.Point(c, c)
      };
    } catch (e) {
      icon = null; // tainted canvas or similar — fall back to the vector marker
    }
  }

  if (!icon) {
    // Vector fallback: surface disc with a thin ring + dot (active: solid ring in ink, larger)
    var fillColor = isActive ? color : surface;
    var dotColor = isActive ? surface : color;
    var sw = isActive ? 2 : 1.25;
    var svg = '<svg xmlns="http://www.w3.org/2000/svg" width="' + size + '" height="' + size + '" viewBox="0 0 40 40">' +
      '<circle cx="20" cy="20" r="18.5" fill="' + fillColor + '" stroke="' + color + '" stroke-width="' + sw + '"/>' +
      '<circle cx="20" cy="20" r="7" fill="' + dotColor + '"/>' +
      '</svg>';
    icon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2)
    };
  }
  this.iconCache[key] = icon;
  return icon;
};

StoreLocator.prototype.addMarkers = function() {
  var self = this;
  
  this.stores.forEach(function(store) {
    if (!store.lat || !store.lng) return;
    
    var marker = new google.maps.Marker({
      position: { lat: parseFloat(store.lat), lng: parseFloat(store.lng) },
      map: self.map,
      icon: self.createMarkerIcon(self.config.markerColor, false),
      title: store.name
    });
    
    marker.storeId = store.id;
    
    marker.addListener('click', function() {
      self.selectStore(store, marker);
    });
    
    marker.addListener('mouseover', function() {
      if (self.activeMarker !== marker) {
        marker.setIcon(self.createMarkerIcon(self.config.markerActiveColor, false));
      }
    });
    
    marker.addListener('mouseout', function() {
      if (self.activeMarker !== marker) {
        marker.setIcon(self.createMarkerIcon(self.config.markerColor, false));
      }
    });
    
    self.markers.push(marker);
    store.marker = marker;
  });
};

StoreLocator.prototype.selectStore = function(store, marker) {
  var self = this;
  
  if (this.activeMarker) {
    this.activeMarker.setIcon(this.createMarkerIcon(this.config.markerColor, false));
  }
  
  if (marker) {
    marker.setIcon(this.createMarkerIcon(this.config.markerActiveColor, true));
    this.activeMarker = marker;
  } else if (store.marker) {
    store.marker.setIcon(this.createMarkerIcon(this.config.markerActiveColor, true));
    this.activeMarker = store.marker;
  }
  
  this.activeStore = store;
  
  document.querySelectorAll('.store-locator__item').forEach(function(item) {
    item.classList.remove('is-active');
    if (item.dataset.storeId === store.id) {
      item.classList.add('is-active');
    }
  });
  
  this.map.panTo({ lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
  if (this.map.getZoom() < 12) {
    this.map.setZoom(12);
  }
  
  this.showDetailPanel(store);
};

StoreLocator.prototype.showDetailPanel = function(store) {
  this.elements.searchPanel.style.display = 'none';
  this.elements.detailPanel.style.display = 'flex';
  
  var fullAddress = store.address + '<br>' + store.city + ', ' + store.state + ' ' + store.zip;
  var directionsUrl = store.directions_url || 
    'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(store.address + ', ' + store.city + ', ' + store.state + ' ' + store.zip);
  
  var hoursData = (window.StoreHours && store.hours_json) ?
    window.StoreHours.render(store.hours_json, store.state) : null;

  var imageHtml = store.image ?
    '<img src="' + store.image + '" alt="' + store.name + '" class="store-locator__detail-image">' :
    '<div class="store-locator__detail-image-placeholder">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">' +
        '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"/>' +
        '<circle cx="12" cy="10" r="3"/>' +
      '</svg>' +
    '</div>';
  
  var phoneDigits = store.phone ? store.phone.replace(/[^0-9+]/g, '') : '';
  var phoneAttr = (store.phone || '').replace(/"/g, '&quot;');
  var phoneHtml = store.phone ?
    '<div class="store-locator__detail-section">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>' +
      '</svg>' +
      '<div class="store-locator__detail-section-content">' +
        '<p><a href="tel:' + phoneDigits + '" data-pg="' + phoneAttr + '">' + store.phone + '</a></p>' +
      '</div>' +
    '</div>' : '';
  
  var emailHtml = store.email ?
    '<div class="store-locator__detail-section">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<rect width="20" height="16" x="2" y="4" rx="2"/>' +
        '<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>' +
      '</svg>' +
      '<div class="store-locator__detail-section-content">' +
        '<p><a href="mailto:' + store.email + '">' + store.email + '</a></p>' +
      '</div>' +
    '</div>' : '';
  
  var hoursHtml = hoursData ?
    '<div class="store-locator__detail-section">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<circle cx="12" cy="12" r="10"/>' +
        '<polyline points="12 6 12 12 16 14"/>' +
      '</svg>' +
      '<div class="store-locator__detail-section-content">' +
        hoursData.statusHtml +
        '<div class="store-locator__hours-list">' + hoursData.listHtml + '</div>' +
      '</div>' +
    '</div>' : '';
  
  var websiteBtn = store.website ?
    '<a href="' + store.website + '" class="store-locator__action-btn store-locator__action-btn--secondary">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
        '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>' +
        '<polyline points="15 3 21 3 21 9"/>' +
        '<line x1="10" x2="21" y1="14" y2="3"/>' +
      '</svg>' +
      'Visit Store Page' +
    '</a>' : '';

  var actionsHtml =
    '<div class="store-locator__detail-actions">' +
      '<a href="' + directionsUrl + '" target="_blank" rel="noopener" class="store-locator__action-btn store-locator__action-btn--primary">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
          '<polygon points="3 11 22 2 13 21 11 13 3 11"/>' +
        '</svg>' +
        'Get Directions' +
      '</a>' +
      websiteBtn +
    '</div>';

  this.elements.detailContent.innerHTML =
    imageHtml +
    '<div class="store-locator__detail-body">' +
      '<h2 class="store-locator__detail-name">' + store.name + '</h2>' +
      actionsHtml +
      '<div class="store-locator__detail-section">' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
          '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z"/>' +
          '<circle cx="12" cy="10" r="3"/>' +
        '</svg>' +
        '<div class="store-locator__detail-section-content">' +
          '<p>' + fullAddress + '</p>' +
        '</div>' +
      '</div>' +
      phoneHtml +
      emailHtml +
      hoursHtml +
    '</div>';
};

StoreLocator.prototype.showSearchPanel = function() {
  this.elements.detailPanel.style.display = 'none';
  this.elements.searchPanel.style.display = 'flex';
  
  if (this.activeMarker) {
    this.activeMarker.setIcon(this.createMarkerIcon(this.config.markerColor, false));
    this.activeMarker = null;
  }
  
  document.querySelectorAll('.store-locator__item').forEach(function(item) {
    item.classList.remove('is-active');
  });
};

StoreLocator.prototype.handleSearch = function() {
  var self = this;
  var query = this.elements.searchInput.value.trim();
  
  if (!query) {
    this.clearSearch();
    return;
  }
  
  var geocoder = new google.maps.Geocoder();
  geocoder.geocode({ address: query + ', USA' }, function(results, status) {
    if (status === 'OK' && results[0]) {
      var location = {
        lat: results[0].geometry.location.lat(),
        lng: results[0].geometry.location.lng()
      };
      self.userLocation = location;
      self.map.setCenter(location);
      self.map.setZoom(8);
      self.filterByDistance(location);
    } else {
      self.filterByText(query);
    }
  });
};

StoreLocator.prototype.filterByDistance = function(center) {
  var self = this;
  
  var storesWithDistance = this.stores.map(function(store) {
    var distance = self.calculateDistance(
      center.lat, center.lng,
      parseFloat(store.lat), parseFloat(store.lng)
    );
    var newStore = {};
    for (var key in store) {
      newStore[key] = store[key];
    }
    newStore.distance = distance;
    return newStore;
  });
  
  this.filteredStores = storesWithDistance
    .filter(function(store) { return store.distance <= self.searchRadius; })
    .sort(function(a, b) { return a.distance - b.distance; });
  
  this.renderStoreList(this.filteredStores);
  this.updateStoreCount(this.filteredStores.length);
  this.updateMarkersVisibility();
  
  if (this.elements.radiusText) {
    this.elements.radiusText.textContent = 'within';
  }
  
  if (this.filteredStores.length > 0) {
    this.fitBoundsToStores(this.filteredStores);
  }
};

StoreLocator.prototype.filterByText = function(query) {
  var lowerQuery = query.toLowerCase();
  
  this.filteredStores = this.stores.filter(function(store) {
    return (store.name && store.name.toLowerCase().indexOf(lowerQuery) !== -1) ||
           (store.city && store.city.toLowerCase().indexOf(lowerQuery) !== -1) ||
           (store.state && store.state.toLowerCase().indexOf(lowerQuery) !== -1) ||
           (store.zip && store.zip.indexOf(query) !== -1);
  });
  
  this.renderStoreList(this.filteredStores);
  this.updateStoreCount(this.filteredStores.length);
  this.updateMarkersVisibility();
  
  if (this.elements.radiusText) {
    this.elements.radiusText.textContent = 'matching';
  }
  
  if (this.filteredStores.length > 0) {
    this.fitBoundsToStores(this.filteredStores);
  }
};

StoreLocator.prototype.clearSearch = function() {
  this.elements.searchInput.value = '';
  this.userLocation = null;
  this.filteredStores = this.stores.slice();
  this.renderStoreList(this.stores);
  this.updateStoreCount(this.stores.length);
  this.updateMarkersVisibility();
  
  if (this.elements.radiusText) {
    this.elements.radiusText.textContent = 'within';
  }
  
  this.map.setCenter(this.config.defaultCenter);
  this.map.setZoom(this.config.defaultZoom);
};

StoreLocator.prototype.getUserLocation = function() {
  var self = this;
  
  if (!navigator.geolocation) {
    alert('Geolocation is not supported by your browser');
    return;
  }
  
  this.elements.useLocationBtn.disabled = true;
  this.elements.useLocationBtn.innerHTML = 
    '<div class="store-locator__spinner" style="width: 18px; height: 18px;"></div>' +
    '<span>Getting location...</span>';
  
  navigator.geolocation.getCurrentPosition(
    function(position) {
      var location = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      self.userLocation = location;
      self.map.setCenter(location);
      self.map.setZoom(10);
      self.filterByDistance(location);
      self.resetLocationButton();
    },
    function(err) {
      console.error('Geolocation error:', err);
      alert('Unable to get your location. Please try searching manually.');
      self.resetLocationButton();
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
  );
};

StoreLocator.prototype.resetLocationButton = function() {
  this.elements.useLocationBtn.disabled = false;
  this.elements.useLocationBtn.innerHTML = 
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">' +
      '<circle cx="12" cy="12" r="3"/>' +
      '<path d="M12 2v3m0 14v3M2 12h3m14 0h3"/>' +
    '</svg>' +
    '<span>Use my current location</span>';
};

StoreLocator.prototype.calculateDistance = function(lat1, lon1, lat2, lon2) {
  var R = 3959;
  var dLat = this.toRad(lat2 - lat1);
  var dLon = this.toRad(lon2 - lon1);
  var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
          Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
          Math.sin(dLon / 2) * Math.sin(dLon / 2);
  var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

StoreLocator.prototype.toRad = function(deg) {
  return deg * (Math.PI / 180);
};

StoreLocator.prototype.renderStoreList = function(stores) {
  var self = this;
  
  if (stores.length === 0) {
    this.elements.storeList.innerHTML = '';
    if (this.elements.empty) {
      this.elements.empty.style.display = 'flex';
    }
    return;
  }
  
  if (this.elements.empty) {
    this.elements.empty.style.display = 'none';
  }
  
  var html = stores.map(function(store) {
    var distanceText = store.distance !== undefined ?
      store.distance.toFixed(1) + ' mi' : '';

    var websiteBtnHtml = store.website ?
      '<a href="' + store.website + '" class="store-locator__item-action">' +
        '<span>Store Page</span>' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
          '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>' +
          '<polyline points="15 3 21 3 21 9"/>' +
          '<line x1="10" x2="21" y1="14" y2="3"/>' +
        '</svg>' +
      '</a>' : '';

    return '<div class="store-locator__item" data-store-id="' + store.id + '">' +
      '<div class="store-locator__item-main">' +
        '<div class="store-locator__item-header">' +
          '<h3 class="store-locator__item-name">' + store.name + '</h3>' +
          (distanceText ? '<span class="store-locator__item-distance">' + distanceText + '</span>' : '') +
        '</div>' +
        '<p class="store-locator__item-address">' + store.address + '<br>' + store.city + ', ' + store.state + ' ' + store.zip + '</p>' +
      '</div>' +
      websiteBtnHtml +
    '</div>';
  }).join('');

  this.elements.storeList.innerHTML = html;

  this.elements.storeList.querySelectorAll('.store-locator__item-action').forEach(function(link) {
    link.addEventListener('click', function(e) {
      e.stopPropagation();
    });
  });

  this.elements.storeList.querySelectorAll('.store-locator__item').forEach(function(item) {
    item.addEventListener('click', function() {
      var storeId = item.dataset.storeId;
      var store = null;
      for (var i = 0; i < self.stores.length; i++) {
        if (self.stores[i].id === storeId) {
          store = self.stores[i];
          break;
        }
      }
      if (store) {
        // TEMP: skip detail panel, go straight to the store page on click
        if (store.website) {
          window.location.href = store.website;
          return;
        }
        self.selectStore(store);
      }
    });
  });
};

StoreLocator.prototype.updateStoreCount = function(count) {
  if (this.elements.storeCount) {
    this.elements.storeCount.textContent = count;
  }
};

StoreLocator.prototype.updateMarkersVisibility = function() {
  var self = this;
  var filteredIds = this.filteredStores.map(function(s) { return s.id; });
  
  this.markers.forEach(function(marker) {
    var isVisible = filteredIds.indexOf(marker.storeId) !== -1;
    marker.setVisible(isVisible);
  });
};

StoreLocator.prototype.fitBoundsToStores = function(stores) {
  var self = this;
  
  if (stores.length === 0) return;
  
  var bounds = new google.maps.LatLngBounds();
  stores.forEach(function(store) {
    if (store.lat && store.lng) {
      bounds.extend({ lat: parseFloat(store.lat), lng: parseFloat(store.lng) });
    }
  });
  
  this.map.fitBounds(bounds, { padding: 50 });
  
  var listener = google.maps.event.addListener(this.map, 'idle', function() {
    if (self.map.getZoom() > 14) {
      self.map.setZoom(14);
    }
    google.maps.event.removeListener(listener);
  });
};

// Initialize when Google Maps is loaded
function initStoreLocator() {
  if (window.storeLocatorInstance) return;

  if (!window.storeLocatorConfig) {
    console.error('Store Locator: Config not found');
    return;
  }
  
  if (typeof google === 'undefined' || !google.maps) {
    console.error('Store Locator: Google Maps not available');
    return;
  }
  
  window.storeLocatorInstance = new StoreLocator(window.storeLocatorConfig);
}

if (window.googleMapsReady && typeof window.googleMapsReady.then === 'function') {
  window.googleMapsReady.then(initStoreLocator).catch(function(err) {
    console.error('Store Locator: Google Maps did not load', err);
  });
} else {
  console.error('Store Locator: googleMapsReady promise missing — check section template');
}